<template>
  <v-dialog v-model="visible" max-width="600px" persistent>
    <v-card>
      <v-card-title class="primary white--text pa-4">
        <v-icon left color="white">mdi-list-status</v-icon>
        Cambiar Estado de Proceso
        <v-spacer></v-spacer>
        <v-btn icon dark @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4">
        <div class="mb-4">
          <div class="text-subtitle-1 font-weight-medium mb-2">
            <v-icon color="primary" left>mdi-car</v-icon>
            {{ vehicle ? `${vehicle.brand} ${vehicle.model}` : '' }}
          </div>
          <div class="text-caption grey--text">
            <v-icon small color="grey" left>mdi-map-marker</v-icon>
            Ubicación actual: {{ vehicle && vehicle.location ? vehicle.location.name : 'Sin ubicación' }}
          </div>
        </div>

        <v-divider class="mb-4"></v-divider>

        <div class="mb-4">
          <!-- Mostrar estado de carga -->
          <div v-if="loadingEstados" class="text-center pa-4">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <div class="mt-2 text-caption">Cargando estados disponibles...</div>
          </div>
          
          <!-- Dropdown con estilos bonitos -->
          <v-select
            v-model="selectedEstado"
            :items="availableEstados"
            item-text="name"
            item-value="id"
            label="Estado de proceso"
            outlined
            dense
            prepend-inner-icon="mdi-list-status"
            :error-messages="errorMessage"
            :disabled="loadingEstados"
            :loading="loadingEstados"
            :no-data-text="availableEstados.length === 0 ? 'No hay estados disponibles para esta ubicación' : 'Cargando...'"
            @change="onEstadoChange"
            clearable
          >
            <template v-slot:selection="{ item }">
              <template v-if="item">
                <v-avatar 
                  :color="item.button_color || '#grey'" 
                  size="24" 
                  class="mr-2"
                >
                  <span 
                    class="white--text text-caption font-weight-bold"
                    :style="{ color: item.text_color || 'white', fontSize: '10px' }"
                  >
                    {{ (item.name || '?').charAt(0).toUpperCase() }}
                  </span>
                </v-avatar>
                {{ item.name || 'Sin nombre' }}
              </template>
            </template>
            
            <template v-slot:item="{ item }">
              <v-list-item-avatar>
                <v-avatar 
                  :color="item.button_color || '#grey'" 
                  size="32"
                >
                  <span 
                    class="white--text text-caption font-weight-bold"
                    :style="{ color: item.text_color || 'white' }"
                  >
                    {{ (item.name || '?').charAt(0).toUpperCase() }}
                  </span>
                </v-avatar>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ item.name || 'Sin nombre' }}</v-list-item-title>
              </v-list-item-content>
            </template>
          </v-select>
        </div>

        <!-- Campo de información adicional -->
        <div class="mb-4">
          <v-textarea
            v-model="additionalInfo"
            label="Información adicional (opcional)"
            outlined
            dense
            rows="5"
            auto-grow
            prepend-inner-icon="mdi-information-outline"
            hint="Puedes agregar comentarios o notas sobre el cambio de estado (los saltos de línea se preservarán)"
            persistent-hint
            counter="500"
            :rules="additionalInfoRules"
          ></v-textarea>
        </div>

        <!-- Sección de imágenes -->
        <div class="mb-4">
          <v-divider class="mb-3"></v-divider>
          
          <image-manager
            vehicle-condition="additional"
            :damage-notes="''"
            :saved-images="[]"
            :new-damage-images="newImages"
            :new-image-previews="newImagePreviews"
            :images-to-remove="[]"
            @update:damageNotes="() => {}"
            @update:newDamageImages="newImages = $event"
            @update:newImagePreviews="newImagePreviews = $event"
            @update:imagesToRemove="() => {}"
            @images-changed="handleImagesChanged"
            @image-removed="() => {}"
          />
        </div>

        <!-- Sección de documentos adicionales -->
        <div class="mb-4">
          <v-divider class="mb-3"></v-divider>
          
          <div class="subtitle-2 mb-3">
            <v-icon left color="primary">mdi-file-document-multiple-outline</v-icon>
            Documentos adicionales
          </div>

          <!-- Sección de nuevos documentos -->
          <div v-if="newDocs && newDocs.length > 0" class="mb-4 new-docs-container">
            <div class="subtitle-2 mb-2">
              <v-icon small left color="success">mdi-file-plus</v-icon>
              Nuevos documentos ({{ newDocs.length }})
            </div>
            <v-list dense>
              <v-list-item
                v-for="(doc, index) in newDocs"
                :key="`new-doc-${index}`"
                class="new-doc-item"
              >
                <v-list-item-avatar>
                  <v-icon :color="getFileIconColor(doc.name)">{{ getFileIcon(doc.name) }}</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ doc.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ formatFileSize(doc.size) }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn
                    icon
                    small
                    color="error"
                    @click="removeNewDoc(index)"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </v-list-item-action>
                <v-chip class="ml-2" x-small color="success">
                  Nuevo
                </v-chip>
              </v-list-item>
            </v-list>
          </div>

          <!-- Input para subir nuevos documentos -->
          <v-file-input
            v-if="canAddMoreDocs"
            v-model="tempDocs"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
            multiple
            label="Agregar documentos adicionales"
            prepend-icon="mdi-paperclip"
            outlined
            dense
            chips
            show-size
            @change="handleDocUpload"
            :hint="`Puedes agregar ${remainingDocSlots} documento(s) más. Máximo 4 documentos. Formatos: PDF, DOC, DOCX, XLS, XLSX, TXT, ZIP, RAR`"
            persistent-hint
          ></v-file-input>

          <v-alert
            v-if="!canAddMoreDocs"
            type="info"
            dense
            class="mt-2"
          >
            <v-icon left small>mdi-information</v-icon>
            Has alcanzado el límite máximo de 4 documentos.
          </v-alert>
        </div>

        <div v-if="errorMessage && !selectedEstado" class="mt-3">
          <v-alert type="error" outlined>
            {{ errorMessage }}
          </v-alert>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          @click="closeDialog"
          color="grey darken-1"
          text
          :disabled="loading"
        >
          Cancelar
        </v-btn>
        <v-btn
          @click="saveEstado"
          color="primary"
          :disabled="!hasChanges || loading"
          :loading="loading"
        >
          <v-icon left>mdi-content-save</v-icon>
          Guardar cambios
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import VehiculoService from '@/services/VehiculoService'
import ImageManager from './ImageManager.vue'

export default {
  name: 'VehicleEstadoProcesoDialog',
  
  components: {
    ImageManager
  },
  
  props: {
    value: {
      type: Boolean,
      default: false
    },
    vehicle: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      visible: false,
      loading: false,
      loadingEstados: false,
      availableEstados: [],
      selectedEstado: null,
      additionalInfo: '',
      errorMessage: '',
      newImages: [],
      newImagePreviews: [],
      newDocs: [],
      tempDocs: [],
      additionalInfoRules: [
        v => !v || v.length <= 500 || 'La información adicional no puede exceder 500 caracteres'
      ]
    }
  },

  watch: {
    value(newVal) {
      this.visible = newVal
      if (newVal && this.vehicle) {
        this.resetDialog()
        this.loadEstadosFromLocation()
      }
    },

    visible(newVal) {
      if (!newVal) {
        this.$emit('input', false)
      }
    },

    // Observar cuando se cargan los estados para establecer el valor por defecto
    availableEstados: {
      handler() {
        if (this.availableEstados.length > 0 && this.vehicle) {
          this.setCurrentEstadoAsDefault()
        }
      },
      immediate: true
    }
  },

  computed: {
    hasChanges() {
      if (!this.vehicle || !this.selectedEstado) return false
      
      const currentEstadoId = this.vehicle.currentVehicleLocationEstadoProceso?.id
      const estadoChanged = this.selectedEstado !== currentEstadoId
      const hasAdditionalInfo = this.additionalInfo && this.additionalInfo.trim().length > 0
      const hasImages = this.newImages && this.newImages.length > 0
      const hasDocs = this.newDocs && this.newDocs.length > 0
      
      return estadoChanged || hasAdditionalInfo || hasImages || hasDocs
    },
    
    canAddMoreDocs() {
      return this.remainingDocSlots > 0
    },
    
    remainingDocSlots() {
      const newCount = this.newDocs ? this.newDocs.length : 0
      return Math.max(0, 4 - newCount)
    }
  },

  methods: {
    resetDialog() {
      this.selectedEstado = null
      this.additionalInfo = ''
      this.errorMessage = ''
      this.availableEstados = []
      this.loadingEstados = false
      this.newImages = []
      this.newImagePreviews = []
      this.newDocs = []
      this.tempDocs = []
    },

    async loadEstadosFromLocation() {
      this.loadingEstados = true
      this.availableEstados = []
      
      if (!this.vehicle || !this.vehicle.location) {
        this.loadingEstados = false
        return
      }

      try {
        // Simular un pequeño delay para asegurar que todo esté listo
        await new Promise(resolve => setTimeout(resolve, 100))
        
        // Obtener los estados de proceso de la ubicación actual del vehículo
        this.availableEstados = this.vehicle.location.estadoProcesos || []
      } catch (error) {
        console.error('Error al cargar estados de la ubicación:', error)
        this.errorMessage = 'Error al cargar los estados disponibles'
        this.availableEstados = []
      } finally {
        this.loadingEstados = false
      }
    },

    setCurrentEstadoAsDefault() {
      if (!this.vehicle || !this.availableEstados.length) {
        this.selectedEstado = null
        return
      }

      if (this.vehicle.currentVehicleLocationEstadoProceso) {
        const currentEstadoId = this.vehicle.currentVehicleLocationEstadoProceso.estadoProceso?.id || this.vehicle.currentVehicleLocationEstadoProceso.id
        
        // Verificar que el estado actual existe en los estados disponibles
        const estadoExists = this.availableEstados.find(estado => estado.id === currentEstadoId)
        
        if (estadoExists) {
          this.selectedEstado = currentEstadoId
        } else {
          this.selectedEstado = this.availableEstados.length > 0 ? this.availableEstados[0].id : null
        }
      } else {
        this.selectedEstado = this.availableEstados.length > 0 ? this.availableEstados[0].id : null
      }
      
      // Forzar reactividad
      this.$nextTick(() => {
      })
    },

    getEstadoById(estadoId) {
      if (!estadoId || !this.availableEstados.length) {
        return { name: '', button_color: '#grey', text_color: '#white' }
      }
      
      const estado = this.availableEstados.find(estado => estado.id === estadoId)
      return estado || { name: '', button_color: '#grey', text_color: '#white' }
    },

    handleImagesChanged(images) {
      this.newImages = images
    },

    onEstadoChange(estadoId) {
      this.errorMessage = ''
    },

    closeDialog() {
      this.visible = false
      this.selectedEstado = null
      this.additionalInfo = ''
      this.errorMessage = ''
      this.newImages = []
      this.newImagePreviews = []
      this.newDocs = []
      this.tempDocs = []
    },
    
    handleDocUpload(files) {
      if (!files || files.length === 0) return

      const existingFiles = this.newDocs || []
      const newFiles = Array.from(files)

      // Verificar que no exceda el límite de 4 documentos
      const totalSlots = 4
      const usedSlots = existingFiles.length
      const availableSlots = totalSlots - usedSlots

      if (availableSlots === 0) {
        this.$toast.warning(`Ya has alcanzado el límite máximo de ${totalSlots} documentos`)
        this.tempDocs = []
        return
      }

      let filesToProcess = newFiles
      let warningMessage = ''

      if (newFiles.length > availableSlots) {
        filesToProcess = newFiles.slice(0, availableSlots)
        const rejected = newFiles.length - availableSlots
        warningMessage = `Solo se agregaron ${availableSlots} documento(s). Se rechazaron ${rejected} documento(s) por exceder el límite de ${totalSlots}.`
      }

      // Combinar archivos existentes con los nuevos
      this.newDocs = [...existingFiles, ...filesToProcess]

      // Mostrar mensaje apropiado
      if (warningMessage) {
        this.$toast.warning(warningMessage)
      } else {
        this.$toast.success(`${filesToProcess.length} documento(s) agregado(s). Total: ${this.newDocs.length}/${totalSlots}`)
      }

      // Resetear el input temporal
      this.$nextTick(() => {
        this.tempDocs = []
      })
    },
    
    removeNewDoc(index) {
      this.newDocs.splice(index, 1)
      this.$toast.info('Documento eliminado')
    },
    
    getFileIcon(filename) {
      if (!filename) return 'mdi-file-outline'
      const extension = filename.split('.').pop().toLowerCase()
      const iconMap = {
        pdf: 'mdi-file-pdf-box',
        doc: 'mdi-file-word-box',
        docx: 'mdi-file-word-box',
        xls: 'mdi-file-excel-box',
        xlsx: 'mdi-file-excel-box',
        txt: 'mdi-file-document-outline',
        zip: 'mdi-folder-zip',
        rar: 'mdi-folder-zip'
      }
      return iconMap[extension] || 'mdi-file-outline'
    },
    
    getFileIconColor(filename) {
      if (!filename) return 'grey'
      const extension = filename.split('.').pop().toLowerCase()
      const colorMap = {
        pdf: 'red',
        doc: 'blue',
        docx: 'blue',
        xls: 'green',
        xlsx: 'green',
        txt: 'grey',
        zip: 'orange',
        rar: 'orange'
      }
      return colorMap[extension] || 'grey'
    },
    
    formatFileSize(bytes) {
      if (!bytes) return '0 KB'
      const kb = bytes / 1024
      if (kb < 1024) {
        return `${kb.toFixed(2)} KB`
      }
      const mb = kb / 1024
      return `${mb.toFixed(2)} MB`
    },

    async saveEstado() {
      if (!this.selectedEstado || !this.vehicle) {
        this.errorMessage = 'Debe seleccionar un estado de proceso'
        return
      }

      // Verificar si realmente cambió el estado
      const currentEstadoId = this.vehicle.currentVehicleLocationEstadoProceso?.id
      if (this.selectedEstado === currentEstadoId && !this.additionalInfo && !this.newImages.length && !this.newDocs.length) {
        this.errorMessage = 'No hay cambios que guardar'
        return
      }

      this.loading = true
      this.errorMessage = ''

      try {
        // Preparar FormData para enviar el estado con imágenes e información adicional
        const formData = new FormData()
        formData.append('estado_proceso_id', this.selectedEstado)
        
        // Agregar información adicional si existe, preservando saltos de línea
        if (this.additionalInfo && this.additionalInfo.trim()) {
          // Preservar saltos de línea convirtiendo \n a formato que el backend entienda
          const infoWithLineBreaks = this.additionalInfo.replace(/\r\n/g, '\n')
          formData.append('additional_info', infoWithLineBreaks)
        }

        // Agregar imágenes si existen - usar _additionalImage[] según la API
        if (this.newImages && this.newImages.length > 0) {
          this.newImages.forEach((image) => {
            formData.append('_additionalImage[]', image)
          })
        }

        // Llamar al servicio para asignar el nuevo estado de proceso con imágenes
        const response = await VehiculoService.assignLocationEstadoProceso(this.vehicle.id, formData)
        
        if (response.error) {
          throw new Error(response.message || 'Error al actualizar el estado')
        }

        // Si hay documentos, enviarlos al endpoint específico
        if (this.newDocs && this.newDocs.length > 0) {
          // Crear FormData para los documentos
          const docsFormData = new FormData()
          this.newDocs.forEach((doc) => {
            docsFormData.append('_additionalDoc[]', doc)
          })
          
          // Enviar documentos al endpoint /estado-proceso/{id}/docs
          const docsResponse = await VehiculoService.addEstadoProcesoDocuments(this.selectedEstado, docsFormData)
          
          if (docsResponse.error) {
            console.error('Error al subir documentos:', docsResponse.message)
            this.$toast.warning('Estado actualizado pero hubo un error al subir los documentos')
          } else {
          }
        }

        this.$toast.success('Estado de proceso actualizado correctamente')
        
        // Emitir evento con el nuevo estado
        const newEstado = this.getEstadoById(this.selectedEstado)
        this.$emit('estado-updated', {
          vehicle: this.vehicle,
          newEstado: newEstado,
          additionalInfo: this.additionalInfo,
          imagesCount: this.newImages.length,
          docsCount: this.newDocs.length
        })
        
        this.closeDialog()
      } catch (error) {
        console.error('Error al actualizar estado de proceso:', error)
        this.errorMessage = error.response?.data?.message || error.message || 'Error al actualizar el estado de proceso'
        this.$toast.error(this.errorMessage)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
/* Estilos específicos para el diálogo de estado de proceso */
.v-dialog .v-card {
  border-radius: 8px;
  overflow: hidden;
}

.v-card-title {
  border-radius: 8px 8px 0 0;
}

.position-relative {
  position: relative;
}

/* Efecto hover para las imágenes en preview */
.v-img:hover + .v-btn {
  opacity: 1;
}

.v-btn[style*="position: absolute"] {
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.v-btn[style*="position: absolute"]:hover {
  opacity: 1;
}

/* Estilos para documentos */
.new-docs-container {
  transition: all 0.3s ease;
}

.new-doc-item {
  background-color: rgba(76, 175, 80, 0.05);
  border-radius: 4px;
  margin-bottom: 4px;
}
</style>
